import { useCallback, useEffect, useRef, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import APIs from "../../Constants/apis.js";
import swal from "sweetalert";
import Pagination from "../../components/CustomComponents/Pagination/pagination.js";
import { logout } from "../../app/auth/authSlice.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import AddUpdateResturantCategoryModal from "./add&updateResturantCategoryModal.jsx";
import "./manageResturantCategories.css";

const apiBaseUrl = (
  process.env.REACT_APP_Environment === "development"
    ? process.env.REACT_APP_API_URL
    : process.env.REACT_APP_API_URL_Production
 || "").replace(/\/$/, "");

const toAbsoluteImageUrl = (value) => {
  if (!value) return "";
  const path = String(value).trim().replace(/\\/g, "/");
  if (/^https?:\/\//i.test(path)) return path;
  if (!apiBaseUrl) return path;
  return `${apiBaseUrl}/${path.replace(/^\//, "")}`;
};

const ManageResturantCategories = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const themeColor = useThemeColor();

  const [search, setSearch] = useState("");
  const [pageSize] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_RESTURANT_CATEGORIES, {
        params: { SortBy: "SortOrder", SortOrder: "ASC", Search: search.trim() || "", PageSize: pageSize, PageNumber: pageNumber },
      });

      if (response.data.status) {
        setCategories(Array.isArray(response.data.data) ? response.data.data : []);
        setTotalRecords(response.data.totalRecords || 0);
        setTotalPages(response.data.totalPages || 1);
      } else {
        showErrorToast(response.data.Message || "Failed to fetch resturant categories");
      }
      setLoading(false);
    } catch (err) {
      setLoading(false);
      if (err.response && err.response.status === 401) {
        dispatch(logout());
        setTimeout(() => navigate("/user/login", { replace: true }), 1000);
      } else {
        showErrorToast("Failed to fetch resturant categories");
      }
    }
  }, [search, pageSize, pageNumber, dispatch, navigate]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleDelete = async (id) => {
    const result = await swal({
      title: "Are you sure?",
      text: "This category will be deleted permanently.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });
    if (!result) return;

    try {
      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_RESTURANT_CATEGORY, { params: { ID: id } });
      if (response.data.status) {
        showSuccessToast("Resturant category deleted successfully");
        fetchCategories();
      } else {
        showErrorToast(response.data.Message || "Failed to delete resturant category");
      }
      setLoading(false);
    } catch {
      setLoading(false);
      showErrorToast("Failed to delete resturant category");
    }
  };

  const handleSubmit = async (data, isEdit) => {
    try {
      setLoading(true);
      const payload = {
        ID: data.id,
        Name: data.name,
        Slug: data.slug,
        SortOrder: data.sortOrder,
        IsActive: data.isActive,
        Image: data.image,
      };

      const response = isEdit
        ? await axiosInstance.put(APIs.PUT_UPDATE_RESTURANT_CATEGORY, payload)
        : await axiosInstance.post(APIs.POST_INSERT_RESTURANT_CATEGORY, payload);

      if (response.data.status) {
        showSuccessToast(isEdit ? "Resturant category updated successfully" : "Resturant category added successfully");
        setShowAddModal(false);
        setShowEditModal(false);
        setCategoryToEdit(null);
        fetchCategories();
      } else {
        showErrorToast(response.data.Message || "Failed to save resturant category");
      }
      setLoading(false);
    } catch {
      setLoading(false);
      showErrorToast("Failed to save resturant category");
    }
  };

  const handleDragStart = (e, index) => {
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.currentTarget.classList.add("is-dragging");
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    setDragOverIndex(index);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverIndex(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const fromIndex = dragItem.current;
    const toIndex = dragOverItem.current;
    setDragOverIndex(null);

    if (fromIndex == null || toIndex == null || fromIndex === toIndex) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    const previous = [...categories];
    const next = [...categories];
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    setCategories(next);

    try {
      const updates = next.map((item, index) => ({
        ID: item.id,
        SortOrder: (pageNumber - 1) * pageSize + index + 1,
      }));
      const response = await axiosInstance.post(APIs.POST_UPDATE_RESTURANT_CATEGORY_SORT_ORDER, {
        Updates: updates,
      });
      if (response.data.status) {
        showSuccessToast("Sort order updated");
        setCategories(
          next.map((item, index) => ({
            ...item,
            sortOrder: (pageNumber - 1) * pageSize + index + 1,
          }))
        );
      } else {
        showErrorToast(response.data.message || response.data.Message || "Failed to update sort order");
        setCategories(previous);
      }
    } catch {
      showErrorToast("Failed to update sort order");
      setCategories(previous);
    } finally {
      dragItem.current = null;
      dragOverItem.current = null;
    }
  };

  const handleDragEnd = (e) => {
    e.currentTarget.classList.remove("is-dragging");
    setDragOverIndex(null);
    dragItem.current = null;
    dragOverItem.current = null;
  };

  return (
    <div className="content rc-manage-page">
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <ol className="breadcrumb m-0">
              <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
              <li className="breadcrumb-item active">Resturant Categories</li>
            </ol>
            <h1 className="page-header mb-0">Manage Resturant Categories</h1>
          </div>
          <button className="btn px-4" onClick={() => setShowAddModal(true)} style={{ backgroundColor: themeColor, border: "none", color: "white", borderRadius: "25px" }}>
            <i className="fa fa-plus me-2"></i>Add Category
          </button>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="mb-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
              <input
                type="text"
                className="form-control"
                style={{ maxWidth: "420px" }}
                placeholder="Search by name or slug..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPageNumber(1); }}
              />
              <span className="text-muted small">Drag rows to reorder</span>
            </div>

            <div className="table-responsive" style={{ minHeight: "200px" }}>
              <table className="table table-hover rc-table">
                <thead>
                  <tr>
                    <th className="rc-drag-col" aria-label="Reorder" />
                    <th>Name</th>
                    <th>Slug</th>
                    <th className="text-center">Sort Order</th>
                    <th className="text-center">Status</th>
                    <th>Image</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="7" className="text-center"><Preloader /></td></tr>
                  ) : categories.length === 0 ? (
                    <tr><td colSpan="7" className="text-center">No categories found</td></tr>
                  ) : (
                    categories.map((item, index) => (
                      <tr
                        key={item.id}
                        draggable
                        className={dragOverIndex === index ? "is-drag-over" : undefined}
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragEnter={(e) => handleDragEnter(e, index)}
                        onDragLeave={handleDragLeave}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        onDragEnd={handleDragEnd}
                      >
                        <td className="rc-drag-col">
                          <span className="rc-drag-handle" title="Drag to reorder" aria-hidden="true">
                            <i className="fa fa-bars" />
                          </span>
                        </td>
                        <td>{item.name}</td>
                        <td>{item.slug}</td>
                        <td className="text-center">{item.sortOrder}</td>
                        <td className="text-center">
                          <span className={`badge ${item.isActive ? "bg-success" : "bg-secondary"}`}>{item.isActive ? "Active" : "Inactive"}</span>
                        </td>
                        <td>
                          {item.image ? (
                            <img
                              src={toAbsoluteImageUrl(item.image)}
                              alt={item.name}
                              style={{ width: "36px", height: "36px", objectFit: "cover", borderRadius: "6px" }}
                            />
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="text-center">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary me-2"
                            draggable={false}
                            onDragStart={(e) => e.stopPropagation()}
                            onClick={() => { setCategoryToEdit(item); setShowEditModal(true); }}
                          >
                            <i className="fa fa-edit"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            draggable={false}
                            onDragStart={(e) => e.stopPropagation()}
                            onClick={() => handleDelete(item.id)}
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="mt-3">
                <Pagination currentPage={pageNumber} totalPages={totalPages} onPageChange={setPageNumber} />
              </div>
            )}
            <p className="mt-3 mb-0">Showing {categories.length} of {totalRecords} categories</p>
          </div>
        </div>
      </div>

      <AddUpdateResturantCategoryModal show={showAddModal} onHide={() => setShowAddModal(false)} onSubmit={handleSubmit} isEdit={false} category={null} />
      <AddUpdateResturantCategoryModal show={showEditModal} onHide={() => { setShowEditModal(false); setCategoryToEdit(null); }} onSubmit={handleSubmit} isEdit={true} category={categoryToEdit} />
    </div>
  );
};

export default ManageResturantCategories;
